// ─────────────────────────────────────────────────────────────────────────────
// Quarters Module — Shared Type Definitions
// Mirrors the backend models in backend/modules/quarters/models/
// ─────────────────────────────────────────────────────────────────────────────

// ── Enumerations ──────────────────────────────────────────────────────────────
import { QuartersApplicationStatus } from "../../../types/status";

export type BuildingStatus = "active" | "inactive";

export type FlatStatus =
  | "vacant"
  | "occupied"
  | "reserved"
  | "maintenance"
  | "locked"
  | "inactive";

export type FurnishedType = "unfurnished" | "semi-furnished" | "fully-furnished";


/** Use the canonical QuartersApplicationStatus enum. Aliased for backward compat. */
export type ApplicationStatus = `${QuartersApplicationStatus}`;

export type AllotmentStatus =
  | "active"
  | "vacated"
  | "cancelled"
  | "extended"
  | "transferred"
  | "evicted";

export type AllotmentType = "automatic" | "manual";

export type WeightFieldType = "boolean" | "numeric" | "option" | "date";

export type ReservationType = "percentage" | "flat";

// ── Building ──────────────────────────────────────────────────────────────────

export interface IBuilding {
  _id: string;
  name: string;
  code: string;
  campus?: string;
  address?: string;
  description?: string;
  status: BuildingStatus;
  totalFloors: number;
  totalFlats: number;
  buildingType?: string;
  buildingCategory?: string;
  /** Quarter type for ALL flats in this building (e.g. "Type I", "Type II"). */
  quarterType: string;
  isDeleted?: boolean;
  deletedAt?: string;
  createdAt: string;
  updatedAt: string;
}

export interface BuildingForm {
  name: string;
  code: string;
  campus: string;
  address: string;
  description: string;
  buildingType: string;
  buildingCategory: string;
  /** quarterType is a building-level field. All flats inherit this from the building. */
  quarterType: string;
  status: BuildingStatus;
}

// ── Floor ─────────────────────────────────────────────────────────────────────

export interface IFloor {
  _id: string;
  buildingId: string | IBuilding;
  name: string;
  floorNumber: number;
  sequence: number;
  numberOfFlats: number;
  description?: string;
  isDeleted?: boolean;
  deletedAt?: string;
  createdAt: string;
  updatedAt: string;
  /** Populated flats (returned by getBuilding endpoint). */
  flats?: IFlat[];
}

/** Floor form — NOTE: quarterType is NOT included (it's a building-level field, not floor). */
export interface FloorForm {
  name: string;
  floorNumber: number;
  numberOfFlats: number;
  description: string;
}

// ── Flat ──────────────────────────────────────────────────────────────────────

/**
 * Flat as returned from API.
 * IMPORTANT: `quarterType` is NOT stored on Flat — it comes from the parent Building.
 * When populated, access it via `buildingId.quarterType`.
 */
export interface IFlat {
  _id: string;
  buildingId: string | IBuilding;
  floorId: string | IFloor;
  flatNumber: string;
  flatName?: string;
  area?: number;
  bedrooms?: number;
  bathrooms?: number;
  furnished: FurnishedType;
  reservedFor?: string | IReservationCategory | null;
  status: FlatStatus;
  isDeleted?: boolean;
  deletedAt?: string;
  createdAt: string;
  updatedAt: string;
}

/** Form state for creating/editing a flat. quarterType is intentionally absent. */
export interface FlatForm {
  buildingId: string;
  floorId: string;
  flatNumber: string;
  flatName: string;
  area: string; // kept as string for controlled input, converted to number on submit
  bedrooms: number;
  bathrooms: number;
  furnished: FurnishedType | string;
  reservedFor: string;
  status: FlatStatus | string;
}

// ── Reservation Category ──────────────────────────────────────────────────────

/** Standard employee category codes used as reservation targets */
export const RESERVATION_CATEGORY_CODES = ["PWD", "SC", "ST", "OBC", "EWS", "UR"] as const;

/** Well-known designations used as reservation targets (admin can add custom ones too) */
export const COMMON_DESIGNATIONS = [
  "Director", "Registrar", "Professor", "Associate Professor", "Assistant Professor",
  "DDA", "AO", "MS", "Deputy Registrar", "Assistant Registrar",
  "JE", "SE", "EE", "SDE", "JTO", "SO", "PA", "Peon",
] as const;

export interface IReservationApplicableTo {
  gender?: string;
  categories?: string[];
  designations?: string[];
}

export interface IReservationCategory {
  _id: string;
  name: string;
  code: string;
  type: ReservationType;
  value: number;
  description?: string;
  /** Target group(s) for this reservation slot */
  applicableTo?: IReservationApplicableTo;
  isDeleted?: boolean;
  createdAt: string;
  updatedAt: string;
}

export type ReservationForm = Omit<
  IReservationCategory,
  "_id" | "isDeleted" | "createdAt" | "updatedAt"
>;

// ── Weight Config ─────────────────────────────────────────────────────────────

export interface IOptionValue {
  optionText: string;
  value: number;
}

export interface IWeightConfig {
  _id?: string;
  fieldKey: string;
  fieldName: string;
  fieldType: WeightFieldType;
  weight: number;
  optionValues?: IOptionValue[];
  isDeleted?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

// ── Employee Details (inside Application) ────────────────────────────────────

export interface IEmployeeDetails {
  employeeId: string;
  department: string;
  designation: string;
  cadre?: string;
  employmentType?: string;
  joiningDate: string;
  instituteJoiningDate?: string;
  retirementDate?: string;
  dob: string;
  age: number | string;
  gender: string;
  maritalStatus: string;
  dependentsCount: number;
  childrenCount: number;
  isPwd: boolean;
  medicalConditions?: string;
  transferStatus?: string;
  currentResidence?: string;
  currentQuarter?: string;
  serviceYears: number;
  previousAllotment?: string;
  disciplinaryStatus?: string;
  emergencyCategory?: string;
  customFields?: Record<string, unknown>;
}

// ── Document Item ─────────────────────────────────────────────────────────────

export interface IDocumentItem {
  name: string;
  url: string;
  key: string;
}

// ── Application ───────────────────────────────────────────────────────────────

export interface IApplication {
  _id: string;
  applicationNumber: string;
  userId: { _id: string; name: string; email: string } | string;
  quarterType: string;
  employeeDetails: IEmployeeDetails;
  status: ApplicationStatus;
  score: number;
  scoreBreakdown?: Record<string, number>;
  documents: IDocumentItem[];
  remarks?: string;
  verificationNotes?: string;
  submittedAt?: string;
  verifiedAt?: string;
  verifiedBy?: { _id: string; name: string } | string | null;
  isDeleted?: boolean;
  deletedAt?: string;
  createdAt: string;
  updatedAt: string;
}

// ── Allotment ─────────────────────────────────────────────────────────────────

export interface IAllotmentHistory {
  status: string;
  changedAt: string;
  changedBy: string;
  remarks?: string;
}

export interface IAllotment {
  _id: string;
  applicationId?: string | null;
  flatId: string | IFlat;
  userId: { _id: string; name: string; email: string } | string;
  allotmentType: AllotmentType;
  allottedAt: string;
  occupancyDate?: string;
  vacatingDate?: string;
  status: AllotmentStatus;
  history: IAllotmentHistory[];
  remarks?: string;
  createdAt: string;
  updatedAt: string;
}

// ── Audit Log ─────────────────────────────────────────────────────────────────

export interface IAuditLog {
  _id: string;
  action: string;
  module: string;
  userId?: { _id: string; name: string; email: string } | null;
  targetId?: string;
  oldValue?: unknown;
  newValue?: unknown;
  ipAddress?: string;
  createdAt: string;
  updatedAt: string;
}

// ── Notification ──────────────────────────────────────────────────────────────

export interface INotification {
  _id: string;
  userId: string;
  title: string;
  message: string;
  type: string;
  isRead: boolean;
  createdAt: string;
  updatedAt: string;
}

// ── Dashboard Stats ───────────────────────────────────────────────────────────

/** Matches the shape returned by GET /quarters/dashboard-stats */
export interface IDashboardStats {
  /** Total active building count */
  buildings: number;
  /** Total active flat count */
  flats: number;
  flatStatus: {
    vacant: number;
    occupied: number;
    reserved: number;
    maintenance: number;
    locked: number;
    inactive: number;
  };
  /** Application count per status key (e.g. "submitted": 5) */
  applications: Record<string, number>;
  /** Flat count per quarter type key (e.g. "Type II": 12) */
  quarterTypes: Record<string, number>;
}

// ── Provisional Allotment (returned by auto-allotment engine) ─────────────────

export interface IProvisionalAllotment {
  applicationId: string;
  applicationNumber: string;
  userId: string;
  userName: string;
  flatId: string;
  flatNumber: string;
  /** quarterType from the building of the flat */
  quarterType: string;
  reason: string;
}

export interface IProvisionalWaitlistItem {
  applicationId: string;
  applicationNumber: string;
  userId: string;
  userName: string;
  score: number;
  waitlistPosition: number;
}

// ── Building detail response (GET /buildings/:id) ─────────────────────────────

export interface IBuildingDetailResponse {
  building: IBuilding;
  floors: IFloor[];
  stats: {
    totalFlats: number;
    vacant: number;
    occupied: number;
    reserved: number;
    maintenance: number;
  };
}

// ── Filter param types ────────────────────────────────────────────────────────

export interface FlatFilterParams {
  buildingId?: string;
  floorId?: string;
  status?: string;
  quarterType?: string;
  q?: string;
  page?: number;
  limit?: number;
}

export interface ApplicationFilterParams {
  status?: string;
  quarterType?: string;
  q?: string;
  page?: number;
  limit?: number;
  sort?: string;
}

export interface AllotmentFilterParams {
  status?: string;
  flatId?: string;
  userId?: string;
  q?: string;
}

export interface AuditLogFilterParams {
  action?: string;
  userId?: string;
  page?: number;
  limit?: number;
}
